import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/shared/ui/toaster";
import { AuthProvider } from "@/features/auth/model/AuthContext";
import { Permission } from "@/shared/types/roles";

// Pages
import Auth from "@/features/auth/pages/Login";
import AuthCallback from "@/features/auth/pages/AuthCallback";
import { ForgotPassword } from "@/features/auth/pages/ForgotPassword";
import ResetPassword from "@/features/auth/pages/ResetPassword";
import Users from "@/features/profiles/pages/Users";
import UserEdit from "@/features/profiles/pages/UserEdit";
import NotFound from "@/shared/widgets/NotFound";
import PresetForm from "@/features/presets/pages/PresetForm";
import { Unauthorized } from "@/shared/widgets/Unauthorized";
import DataDeletionPage from "@/features/lgpd/pages/DataDeletionPage";
import { PackGrid, PackForm } from "@/features/packs/pages";
import StoreRedirectPage from "@/features/store-redirect/pages/StoreRedirectPage";

// Components
import { ProtectedRoute } from "@/shared/widgets/auth/ProtectedRoute";
import { CreatorRoute } from "@/shared/widgets/auth/CreatorRoute";
import { AppLayout } from "@/shared/widgets/layout/AppLayout";
import {
  BankAccountsPage,
  ReceivablesDashboardPage,
  PaymentsHistoryPage,
  SolicitacoesPagamentoPage,
} from "@/features/creator-financial";

// Features
import AnalyticsPage from "@/features/analytics/pages/AnalyticsPage";
import AppAnalyticsPage from "@/features/app-analytics/pages/AppAnalyticsPage";
import AppFinancialPage from "@/features/app-financial/pages/AppFinancialPage";
import Tags from "@/features/tags/pages/index";
import TagCreateUpdate from "@/features/tags/pages/create-update";
import LearningsIndex from "@/features/learnings/pages/LearningIndex";
import LearningEdit from "@/features/learnings/pages/LearningEdit";
import CategoriesIndex from "@/features/categories/pages/CategoriesIndex";
import CategoryEdit from "@/features/categories/pages/CategoryEdit";
import { ContractsList, ContractForm } from "@/features/contracts/pages";
import HighlightManagementPage from "@/features/highlights/pages/HighlightManagementPage";
import { ReceivableForm, ReceivablesList } from "@/features/receivables/pages";
import { AdminFinancialOverviewPage } from "@/features/admin-financial-overview";
import { ReceivableShareConfigList, ReceivableShareConfigForm } from "@/features/receivable-share-configs/pages";
import { PaymentsList, PaymentForm } from "@/features/payments/pages";
import { ReceivableSnapshotsList } from "@/features/receivable-snapshots/pages";
import { CollectionsList, CollectionForm } from "@/features/collections/pages";
import { BannersList, BannerForm } from "@/features/banners/pages";
import {
  CampaignsListPage,
  CampaignFormPage,
  DispatchesPage,
} from "@/features/bulk-notifications";
import DiscordIntegrationPage from "@/features/integrations/pages/DiscordIntegrationPage";
import MailchimpIntegrationPage from "@/features/integrations/pages/MailchimpIntegrationPage";
import AmplitudeAnalyticsPage from "@/features/amplitude/pages/AmplitudeAnalyticsPage";
import AccountEventsPage from "@/features/account-events/pages/AccountEventsPage";
import {
  TemplatesListPage,
  TemplateEditorPage,
  TutorialAddTemplatePage,
  TutorialPsdGuidePage,
} from "@/features/psd-templates";

function App() {
  // Move queryClient inside the component
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter basename={import.meta.env.BASE_URL || "/"}>
          <Routes>
            {/* Rotas públicas (sem sidebar) */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/exclusao-dados" element={<DataDeletionPage />} />
            <Route path="/store-redirect" element={<StoreRedirectPage />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/tutorials/add-template" element={<TutorialAddTemplatePage />} />
            <Route path="/tutorials/psd-guide" element={<TutorialPsdGuidePage />} />

            {/* Rotas protegidas (com sidebar) */}
            <Route
              path="/"
              element={
                <ProtectedRoute permissions={[Permission.PACKS_VIEW]}>
                  <AppLayout>
                    <PackGrid />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stats"
              element={
                <ProtectedRoute permissions={[Permission.ANALYTICS_VIEW]}>
                  <AppLayout>
                    <AnalyticsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app-analytics"
              element={
                <ProtectedRoute permissions={[Permission.ANALYTICS_VIEW]}>
                  <AppLayout>
                    <AppAnalyticsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app-financial"
              element={
                <ProtectedRoute permissions={[Permission.ANALYTICS_VIEW]}>
                  <AppLayout>
                    <AppFinancialPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute permissions={[Permission.USERS_VIEW]}>
                  <AppLayout>
                    <Users />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:id/edit"
              element={
                <ProtectedRoute permissions={[Permission.USERS_EDIT]}>
                  <AppLayout>
                    <UserEdit />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts"
              element={
                <ProtectedRoute permissions={[Permission.CONTRACTS_VIEW]}>
                  <AppLayout>
                    <ContractsList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/new"
              element={
                <ProtectedRoute permissions={[Permission.CONTRACTS_EDIT]}>
                  <AppLayout>
                    <ContractForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/:id"
              element={
                <ProtectedRoute permissions={[Permission.CONTRACTS_EDIT]}>
                  <AppLayout>
                    <ContractForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/packs/new"
              element={
                <ProtectedRoute permissions={[Permission.PACKS_CREATE]}>
                  <AppLayout>
                    <PackForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/packs/:id"
              element={
                <ProtectedRoute permissions={[Permission.PACKS_VIEW]}>
                  <AppLayout>
                    <PackForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/presets/new"
              element={
                <ProtectedRoute permissions={[Permission.PRESETS_EDIT]}>
                  <AppLayout>
                    <PresetForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/presets/:id"
              element={
                <ProtectedRoute permissions={[Permission.PRESETS_EDIT]}>
                  <AppLayout>
                    <PresetForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tags"
              element={
                <ProtectedRoute permissions={[Permission.TAGS_VIEW]}>
                  <AppLayout>
                    <Tags />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tags/new"
              element={
                <ProtectedRoute permissions={[Permission.TAGS_CREATE]}>
                  <AppLayout>
                    <TagCreateUpdate />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tags/edit/:id"
              element={
                <ProtectedRoute permissions={[Permission.TAGS_EDIT]}>
                  <AppLayout>
                    <TagCreateUpdate />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/learning"
              element={
                <ProtectedRoute permissions={[Permission.LEARNING_VIEW]}>
                  <AppLayout>
                    <LearningsIndex />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/learning/new"
              element={
                <ProtectedRoute permissions={[Permission.LEARNING_EDIT]}>
                  <AppLayout>
                    <LearningEdit />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/learning/edit/:id"
              element={
                <ProtectedRoute permissions={[Permission.LEARNING_EDIT]}>
                  <AppLayout>
                    <LearningEdit />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <ProtectedRoute permissions={[Permission.CATEGORIES_VIEW]}>
                  <AppLayout>
                    <CategoriesIndex />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories/new"
              element={
                <ProtectedRoute permissions={[Permission.CATEGORIES_EDIT]}>
                  <AppLayout>
                    <CategoryEdit />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories/edit/:id"
              element={
                <ProtectedRoute permissions={[Permission.CATEGORIES_EDIT]}>
                  <AppLayout>
                    <CategoryEdit />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/highlights"
              element={
                <ProtectedRoute permissions={[Permission.HIGHLIGHTS_VIEW]}>
                  <AppLayout>
                    <HighlightManagementPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/financial-overview"
              element={
                <ProtectedRoute permissions={[Permission.RECEIVABLES_VIEW]}>
                  <AppLayout>
                    <AdminFinancialOverviewPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/mobile/collections/">
              <Route
                path=""
                element={
                  <ProtectedRoute permissions={[Permission.HIGHLIGHTS_VIEW]}>
                    <AppLayout>
                      <CollectionsList />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="new"
                element={
                  <ProtectedRoute permissions={[Permission.HIGHLIGHTS_EDIT]}>
                    <AppLayout>
                      <CollectionForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedRoute permissions={[Permission.HIGHLIGHTS_EDIT]}>
                    <AppLayout>
                      <CollectionForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="/mobile/banners/">
              <Route
                path=""
                element={
                  <ProtectedRoute permissions={[Permission.HIGHLIGHTS_VIEW]}>
                    <AppLayout>
                      <BannersList />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="new"
                element={
                  <ProtectedRoute permissions={[Permission.HIGHLIGHTS_EDIT]}>
                    <AppLayout>
                      <BannerForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedRoute permissions={[Permission.HIGHLIGHTS_EDIT]}>
                    <AppLayout>
                      <BannerForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="/receivables/"

            >
              <Route path="" element={
                <ProtectedRoute permissions={[Permission.RECEIVABLES_VIEW]}>
                  <AppLayout>
                    <ReceivablesList />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="new" element={
                <ProtectedRoute permissions={[Permission.RECEIVABLES_EDIT]}>
                  <AppLayout>
                    <ReceivableForm />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path=":id" element={
                <ProtectedRoute permissions={[Permission.RECEIVABLES_EDIT]}>
                  <AppLayout>
                    <ReceivableForm />
                  </AppLayout>
                </ProtectedRoute>
              } />
            </Route>
            <Route path="/receivable-share-configs/">
              <Route
                path=""
                element={
                  <ProtectedRoute permissions={[Permission.RECEIVABLES_VIEW]}>
                    <AppLayout>
                      <ReceivableShareConfigList />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="new"
                element={
                  <ProtectedRoute permissions={[Permission.RECEIVABLES_EDIT]}>
                    <AppLayout>
                      <ReceivableShareConfigForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedRoute permissions={[Permission.RECEIVABLES_EDIT]}>
                    <AppLayout>
                      <ReceivableShareConfigForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="/receivable-snapshots"
              element={
                <ProtectedRoute permissions={[Permission.RECEIVABLES_VIEW]}>
                  <AppLayout>
                    <ReceivableSnapshotsList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications/campaigns"
              element={
                <ProtectedRoute permissions={[Permission.NOTIFICATIONS_VIEW]}>
                  <AppLayout>
                    <CampaignsListPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications/campaigns/new"
              element={
                <ProtectedRoute permissions={[Permission.NOTIFICATIONS_EDIT]}>
                  <AppLayout>
                    <CampaignFormPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications/campaigns/:id"
              element={
                <ProtectedRoute permissions={[Permission.NOTIFICATIONS_EDIT]}>
                  <AppLayout>
                    <CampaignFormPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications/campaigns/:id/dispatches"
              element={
                <ProtectedRoute permissions={[Permission.NOTIFICATIONS_VIEW]}>
                  <AppLayout>
                    <DispatchesPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/payments/">
              <Route
                path=""
                element={
                  <ProtectedRoute permissions={[Permission.PAYMENTS_VIEW]}>
                    <AppLayout>
                      <PaymentsList />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="new"
                element={
                  <ProtectedRoute permissions={[Permission.PAYMENTS_CREATE, Permission.PAYMENTS_EDIT]}>
                    <AppLayout>
                      <PaymentForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedRoute permissions={[Permission.PAYMENTS_EDIT]}>
                    <AppLayout>
                      <PaymentForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
            </Route>
            {/* Portal do Criador (apenas PHOTOGRAPHER / INFLUENCER) */}
            <Route
              path="/creator"
              element={
                <CreatorRoute>
                  <AppLayout>
                    <ReceivablesDashboardPage />
                  </AppLayout>
                </CreatorRoute>
              }
            />
            <Route
              path="/creator/receivables"
              element={
                <CreatorRoute>
                  <AppLayout>
                    <ReceivablesDashboardPage />
                  </AppLayout>
                </CreatorRoute>
              }
            />
            <Route
              path="/creator/bank-accounts"
              element={
                <CreatorRoute>
                  <AppLayout>
                    <BankAccountsPage />
                  </AppLayout>
                </CreatorRoute>
              }
            />
            <Route
              path="/creator/payments"
              element={
                <CreatorRoute>
                  <AppLayout>
                    <PaymentsHistoryPage />
                  </AppLayout>
                </CreatorRoute>
              }
            />
            <Route
              path="/creator/solicitacoes-pagamento"
              element={
                <CreatorRoute>
                  <AppLayout>
                    <SolicitacoesPagamentoPage />
                  </AppLayout>
                </CreatorRoute>
              }
            />
            <Route
              path="/integrations/discord"
              element={
                <ProtectedRoute permissions={[Permission.SETTINGS_VIEW]}>
                  <AppLayout>
                    <DiscordIntegrationPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations/mailchimp"
              element={
                <ProtectedRoute permissions={[Permission.SETTINGS_VIEW]}>
                  <AppLayout>
                    <MailchimpIntegrationPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/amplitude"
              element={
                <ProtectedRoute permissions={[Permission.ANALYTICS_VIEW]}>
                  <AppLayout>
                    <AmplitudeAnalyticsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/account-events"
              element={
                <ProtectedRoute permissions={[Permission.ANALYTICS_VIEW]}>
                  <AppLayout>
                    <AccountEventsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/psd-templates"
              element={
                <ProtectedRoute permissions={[Permission.TEMPLATES_VIEW]}>
                  <AppLayout>
                    <TemplatesListPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/psd-templates/new"
              element={
                <ProtectedRoute permissions={[Permission.TEMPLATES_EDIT]}>
                  <AppLayout>
                    <TemplateEditorPage mode="new" />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/psd-templates/:id"
              element={
                <ProtectedRoute permissions={[Permission.TEMPLATES_EDIT]}>
                  <AppLayout>
                    <TemplateEditorPage mode="edit" />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Rota 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider >
  );
}

export default App;
